// Campo de destinatarios con sugerencias (D60): fichas que se quitan con la x y un campo que, al escribir 2 letras o
// mas, sugiere del equipo, los contactos del CRM y las personas con quienes se ha escrito (solo de buzones que puedes
// leer). Flechas para moverse, Enter o Tab para elegir; coma o punto y coma agregan lo escrito tal cual.
import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { buscarContactos, type ContactoSugerido } from '../api/correo';
import { useDebounced } from '../hooks';
import { destinatarios } from '../lib/correo';
import { Icon } from '../lib/icons';
import { toast } from './toasts';

const ORIGEN: Record<ContactoSugerido['origen'], string> = { equipo: 'Equipo', contacto: 'Contacto', correo: '' };

export function CampoCorreos({ etiqueta, lista, cambiar, desactivado, aviso }: { etiqueta: string; lista: string[]; cambiar: (l: string[]) => void; desactivado?: boolean; aviso?: string }) {
  const [nuevo, setNuevo] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [activo, setActivo] = useState(0);
  const id = useId();
  const q = useDebounced(nuevo.trim(), 200);
  const r = useQuery({ queryKey: ['contactos-sugeridos', q.toLowerCase()], queryFn: () => buscarContactos(q), enabled: q.length >= 2, staleTime: 60_000 });
  const ya = new Set(lista.map((x) => x.toLowerCase()));
  const opciones = (q.length >= 2 && nuevo.trim().length >= 2 ? (r.data ?? []) : []).filter((c) => !ya.has(c.correo));
  const mostrar = abierto && opciones.length > 0;

  const elegir = (c: ContactoSugerido) => {
    cambiar([...new Set([...lista, c.correo])]);
    setNuevo('');
    setActivo(0);
  };
  const agregar = () => {
    const { validos, invalidos } = destinatarios(nuevo);
    if (invalidos.length) toast(`No es un correo válido: ${invalidos.join(', ')}`, 'error');
    if (validos.length) cambiar([...new Set([...lista, ...validos])]);
    setNuevo(invalidos.join(', '));
  };

  return (
    <div className={`resp-dest campo-correos ${desactivado ? 'apagado' : ''}`} title={desactivado ? aviso : undefined}>
      <span className="resp-et">{etiqueta}</span>
      {lista.map((x) => (
        <span key={x} className="chip">
          {x}
          <button type="button" aria-label={`Quitar ${x}`} onClick={() => cambiar(lista.filter((y) => y !== x))}>
            <Icon name="x" size={11} />
          </button>
        </span>
      ))}
      <input
        value={nuevo}
        disabled={desactivado}
        role="combobox"
        aria-expanded={mostrar}
        aria-controls={`${id}-lista`}
        aria-autocomplete="list"
        aria-activedescendant={mostrar ? `${id}-${activo}` : undefined}
        onChange={(e) => (setNuevo(e.target.value), setAbierto(true), setActivo(0))}
        onFocus={() => setAbierto(true)}
        onKeyDown={(e) => {
          if (mostrar && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            setActivo((a) => (a + (e.key === 'ArrowDown' ? 1 : opciones.length - 1)) % opciones.length);
            return;
          }
          if (mostrar && (e.key === 'Enter' || e.key === 'Tab') && opciones[activo]) {
            e.preventDefault();
            elegir(opciones[activo]);
            return;
          }
          if (mostrar && e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setAbierto(false);
            return;
          }
          if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
            e.preventDefault();
            agregar();
          }
          if (e.key === 'Backspace' && !nuevo && lista.length) cambiar(lista.slice(0, -1));
        }}
        onBlur={() => {
          // Despues del clic en una sugerencia (que usa onMouseDown).
          setTimeout(() => setAbierto(false), 120);
          if (nuevo.trim() && /@/.test(nuevo)) agregar();
        }}
        placeholder={desactivado ? aviso : lista.length ? '' : 'Nombre o correo…'}
        aria-label={`Agregar en ${etiqueta}`}
      />
      {mostrar ? (
        <ul className="campo-correos-lista" role="listbox" id={`${id}-lista`}>
          {opciones.map((c, i) => (
            <li
              key={c.correo}
              id={`${id}-${i}`}
              role="option"
              aria-selected={i === activo}
              onMouseDown={(e) => (e.preventDefault(), elegir(c))}
              onMouseEnter={() => setActivo(i)}
            >
              <span className="cc-txt">
                <b>{c.nombre || c.correo}</b>
                {c.nombre ? <span>{c.correo}</span> : null}
              </span>
              {ORIGEN[c.origen] ? <span className="cc-origen">{ORIGEN[c.origen]}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
